import b4a from 'b4a'
import z32 from 'z32'
import c from 'compact-encoding'
import * as C from './primitives.js'

export const INVITE_VERSION = 1

/**
 * Blind invites.
 *
 * An invite code carries a random 32-byte secret and nothing else that is
 * linkable to the room. The pairing topic is H(secret), so an observer who
 * watches the DHT learns only that *some* pairing is happening — not which
 * room, and not who is in it. The room key itself is only revealed inside a
 * channel that is both symmetrically encrypted with a key derived from the
 * secret and sealed to the joiner's account key.
 */
export const inviteEncoding = {
  preencode (state, i) {
    c.uint.preencode(state, i.version)
    c.fixed32.preencode(state, i.secret)
    c.uint.preencode(state, i.kind)
    c.string.preencode(state, i.hint || '')
  },
  encode (state, i) {
    c.uint.encode(state, i.version)
    c.fixed32.encode(state, i.secret)
    c.uint.encode(state, i.kind)
    c.string.encode(state, i.hint || '')
  },
  decode (state) {
    return {
      version: c.uint.decode(state),
      secret: c.fixed32.decode(state),
      kind: c.uint.decode(state),
      hint: c.string.decode(state)
    }
  }
}

export const INVITE_KIND = { ROOM: 0, CHANNEL: 1, CONTACT: 2 }

export class Invite {
  constructor ({ secret, kind = INVITE_KIND.ROOM, hint = '', expires = 0, uses = 0, role = 'member' }) {
    this.secret = C.toBuffer(secret)
    this.kind = kind
    this.hint = hint
    this.expires = expires
    this.uses = uses // 0 = unlimited
    this.used = 0
    this.role = role
    this.revoked = false
  }

  static create (opts = {}) {
    return new Invite({ ...opts, secret: C.randomBytes(32) })
  }

  /** Swarm topic both sides meet on. Reveals nothing about the room. */
  get id () {
    return C.domainHash('pine/invite/topic/v1', this.secret)
  }

  get requestKey () {
    return C.derive(this.secret, 'pine-req', 1)
  }

  get responseKey () {
    return C.derive(this.secret, 'pine-res', 2)
  }

  get code () {
    const payload = { version: INVITE_VERSION, secret: this.secret, kind: this.kind, hint: this.hint }
    return z32.encode(encodeWith(inviteEncoding, payload))
  }

  link (origin = 'https://papi.olshub.xyz') {
    return origin + '/i/' + this.code
  }

  get expired () {
    if (this.revoked) return true
    if (this.expires && Date.now() > this.expires) return true
    if (this.uses && this.used >= this.uses) return true
    return false
  }

  static parse (input) {
    let code = String(input).trim()
    const m = code.match(/(?:pine:\/\/|\/i\/|[?&]i=)([a-z0-9]+)/i)
    if (m) code = m[1]
    code = code.replace(/[^a-z0-9]/gi, '')

    let raw
    try {
      raw = z32.decode(code.toLowerCase())
    } catch {
      throw new Error('That does not look like a Pine invite')
    }
    let payload
    try {
      payload = decodeWith(inviteEncoding, raw)
    } catch {
      throw new Error('This invite is malformed')
    }
    if (payload.version !== INVITE_VERSION) throw new Error('This invite needs a newer version of Pine')
    return new Invite({ secret: payload.secret, kind: payload.kind, hint: payload.hint })
  }

  toJSON () {
    return {
      secret: b4a.toString(this.secret, 'hex'),
      kind: this.kind,
      hint: this.hint,
      expires: this.expires,
      uses: this.uses,
      used: this.used,
      role: this.role,
      revoked: this.revoked,
      code: this.code
    }
  }

  static fromJSON (j) {
    const inv = new Invite({
      secret: b4a.from(j.secret, 'hex'),
      kind: j.kind,
      hint: j.hint,
      expires: j.expires,
      uses: j.uses,
      role: j.role
    })
    inv.used = j.used || 0
    inv.revoked = !!j.revoked
    return inv
  }
}

/* ---- pairing wire messages ---- */

export const pairRequestEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.account)
    c.string.preencode(state, m.name)
    c.fixed32.preencode(state, m.nonce)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.account)
    c.string.encode(state, m.name)
    c.fixed32.encode(state, m.nonce)
  },
  decode (state) {
    return {
      account: c.fixed32.decode(state),
      name: c.string.decode(state),
      nonce: c.fixed32.decode(state)
    }
  }
}

export const pairResponseEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.roomKey)
    c.fixed32.preencode(state, m.encryptionKey)
    c.fixed32.preencode(state, m.nonce)
    c.string.preencode(state, m.meta)
    c.fixed32.preencode(state, m.inviter)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.roomKey)
    c.fixed32.encode(state, m.encryptionKey)
    c.fixed32.encode(state, m.nonce)
    c.string.encode(state, m.meta)
    c.fixed32.encode(state, m.inviter)
  },
  decode (state) {
    return {
      roomKey: c.fixed32.decode(state),
      encryptionKey: c.fixed32.decode(state),
      nonce: c.fixed32.decode(state),
      meta: c.string.decode(state),
      inviter: c.fixed32.decode(state)
    }
  }
}

/**
 * Third leg of the handshake: once the joiner has the room key it opens its
 * Autobase locally and reports the public key of its own writer core, which
 * the host then authorises with a WRITER_ADD op.
 */
export const pairWriterEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.account)
    c.fixed32.preencode(state, m.writerKey)
    c.fixed32.preencode(state, m.nonce)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.account)
    c.fixed32.encode(state, m.writerKey)
    c.fixed32.encode(state, m.nonce)
  },
  decode (state) {
    return {
      account: c.fixed32.decode(state),
      writerKey: c.fixed32.decode(state),
      nonce: c.fixed32.decode(state)
    }
  }
}

export function encodePairWriter (msg, key) {
  return C.encrypt(encodeWith(pairWriterEncoding, msg), key)
}

export function decodePairWriter (ciphertext, key) {
  const raw = C.decrypt(ciphertext, key)
  if (!raw) return null
  try { return decodeWith(pairWriterEncoding, raw) } catch { return null }
}

export function encodePairRequest (msg, key) {
  return C.encrypt(encodeWith(pairRequestEncoding, msg), key)
}

export function decodePairRequest (ciphertext, key) {
  const raw = C.decrypt(ciphertext, key)
  if (!raw) return null
  try { return decodeWith(pairRequestEncoding, raw) } catch { return null }
}

export function encodePairResponse (msg, key, recipientBoxKey) {
  const inner = C.encrypt(encodeWith(pairResponseEncoding, msg), key)
  return C.seal(inner, recipientBoxKey)
}

export function decodePairResponse (ciphertext, key, boxPublicKey, boxSecretKey) {
  const inner = C.sealOpen(ciphertext, boxPublicKey, boxSecretKey)
  if (!inner) return null
  const raw = C.decrypt(inner, key)
  if (!raw) return null
  try { return decodeWith(pairResponseEncoding, raw) } catch { return null }
}

export function encodeWith (enc, value) {
  const state = c.state()
  enc.preencode(state, value)
  state.buffer = b4a.allocUnsafe(state.end)
  enc.encode(state, value)
  return state.buffer
}

export function decodeWith (enc, buffer) {
  return enc.decode({ start: 0, end: buffer.byteLength, buffer })
}
